/**
 * Hook to calculate the AI score and return category name
 * @param {number} aiScore - The AI score to calculate
 * @param {string} version - The version of the AI score, default undefined
 * @param {boolean} aiDetectionStrictMode - Whether the AI detection is in strict mode
 * @returns {object {aiScore: number, category: "AiGenerated" | "ProbablyAiGenerated" | "Unsure" | "ProbablyOriginal" | "Original"}} The calculated AI score and category name
 */
export const calculateAndCategorizeAIScore = (aiScore, version, aiDetectionStrictMode) => {
    const calculatedAIScore = calculateAIScore(aiScore, version, aiDetectionStrictMode);
    const categoryName = calculateCategoryName(aiScore, version, aiDetectionStrictMode);

    return { aiScore: calculatedAIScore, category: categoryName };
};

const calculateAIScore = (aiScore, version, aiDetectionStrictMode) => {
    //For strict mode
    if (aiDetectionStrictMode) {
        return Math.round(aiScore * 10000) / 100;
    }

    //For balanced mode
    if (version && (version === "5.1" || version === "5.0" || version === "4.0")) {
        return Math.round(aiScore * 10000) / 100;
    }

    if (version && version === "3.0") {
        return Math.round(aiScore * 10000) / 100;
    }

    if (version && version === "2.1") {
        return calculateClassicalRegressionModelScore(aiScore, 0.9999);
    }

    return calculateClassicalRegressionModelScore(aiScore, 0.9995);
};

const calculateClassicalRegressionModelScore = (aiScore, upperTreshold) => {
    let probabilityOfCorrectEvaluationUnrounded = 0;
    if (aiScore >= upperTreshold) probabilityOfCorrectEvaluationUnrounded = 0.9999;

    //Regression model 0.9 < x < 0.9995
    const a = 0.9731173590718;
    const b = 34.3321921960834;

    if (aiScore >= 0.9 && aiScore < upperTreshold) probabilityOfCorrectEvaluationUnrounded = a * Math.pow(aiScore, b);

    let probabilityOfCorrectEvaluation = Math.round(probabilityOfCorrectEvaluationUnrounded * 10000) / 100;

    if (probabilityOfCorrectEvaluation === 100) probabilityOfCorrectEvaluation = 99.99;
    if (probabilityOfCorrectEvaluation === 0) probabilityOfCorrectEvaluation = 0.1;

    return probabilityOfCorrectEvaluation;
};

const calculateCategoryName = (aiScore, version, aiDetectionStrictMode) => {
    //For strict mode
    if (aiDetectionStrictMode) {
        if (aiScore >= 0.9) return "AiGenerated";
        if (aiScore >= 0.8) return "ProbablyAiGenerated";
        if (aiScore >= 0.6) return "Unsure";
        if (aiScore >= 0.3) return "ProbablyOriginal";
        return "Original";
    }

    //For balanced mode
    if(version && (version === "5.1" || version === "5.0" || version === "4.0")) {
        if (aiScore >= 0.9) return "AiGenerated";
        if (aiScore >= 0.8) return "ProbablyAiGenerated";
        if (aiScore >= 0.6) return "Unsure";
        if (aiScore >= 0.3) return "ProbablyOriginal";
        return "Original";
    }

    if(version && version === "3.0") {
        if (aiScore >= 0.9) return "AiGenerated";
        if (aiScore >= 0.8) return "ProbablyAiGenerated";
        if (aiScore >= 0.6) return "Unsure";
        if (aiScore >= 0.3) return "ProbablyOriginal";
        return "Original";
    }

    if(version && version === "2.1") {
        if (aiScore >= 0.999) return "AiGenerated";
        if (aiScore >= 0.983) return "ProbablyAiGenerated";
        if (aiScore >= 0.9) return "Unsure";
        if (aiScore >= 0.5) return "ProbablyOriginal";
        return "Original";
    }
    
    if (aiScore >= 0.999) return "AiGenerated";
    if (aiScore >= 0.9) return "Unsure";
    if (aiScore >= 0.5) return "ProbablyOriginal";
    return "Original";
}